import React, { useEffect, useRef, useState } from 'react';
import { passkeyLogin, passkeysSupported, explain, isCancel, type AuthStatus } from './authApi';
import AuthShell, { FaceIdIcon, GoogleG, Notices, Spinner, primaryBtn, type NoticeData } from './AuthShell';

export type LoginNotice = 'expired' | 'loggedOut' | 'denied' | null;

interface Props {
  status: AuthStatus;
  onLoggedIn: () => void;
  /** true = shown over a running app (session expired): patient state stays intact behind. */
  overlay?: boolean;
  /** Why the person is seeing the login (session ended, signed out, Google said no). */
  notice?: LoginNotice;
}

const NOTICES: Record<Exclude<LoginNotice, null>, NoticeData> = {
  expired: { kind: 'info', text: 'Sesja wygasła — zaloguj się ponownie.' },
  loggedOut: { kind: 'success', text: 'Wylogowano.' },
  // Deliberately the same text for "no such account", "blocked" and "Google refused": no account enumeration.
  denied: { kind: 'error', text: 'Nie udało się zalogować. Konto może być zablokowane lub bez dostępu — skontaktuj się z administratorem.' },
};

const UNSUPPORTED = 'Ta przeglądarka nie obsługuje passkeyów. Użyj aktualnej przeglądarki Safari lub Chrome na zaktualizowanym systemie.';

const LoginScreen: React.FC<Props> = ({ status, onLoggedIn, overlay, notice }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<NoticeData | null>(null);
  const busyRef = useRef(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const supported = passkeysSupported();

  useEffect(() => { if (overlay) btnRef.current?.focus(); }, [overlay]);

  const go = async () => {
    if (busyRef.current) return; // double-click guard
    busyRef.current = true; setBusy(true); setErr(null);
    try { await passkeyLogin(); onLoggedIn(); }
    catch (e) {
      setErr(isCancel(e)
        ? { kind: 'info', text: 'Logowanie anulowane. Gdy będziesz gotowy, spróbuj ponownie.' }
        : { kind: 'error', text: explain(e) });
      busyRef.current = false; setBusy(false);
    }
  };

  const shown = notice ?? (overlay ? 'expired' : null);
  return (
    <AuthShell overlay={overlay} title="Zaloguj się"
      subtitle={overlay ? 'Dane na ekranie zostały zachowane — wrócisz dokładnie tam, gdzie skończyłeś.' : 'Bezpieczne logowanie dla osób z dostępem'}>
      <div className="space-y-3">
        <Notices items={[shown && NOTICES[shown], !supported && { kind: 'error', text: UNSUPPORTED }, err]} />
        <button ref={btnRef} type="button" onClick={go} disabled={busy || !supported} aria-busy={busy} className={primaryBtn}>
          {busy ? <Spinner /> : <FaceIdIcon />}
          <span>{busy ? 'Potwierdź na urządzeniu…' : 'Zaloguj się passkeyem'}</span>
        </button>
        {status.google && (
          <>
            <div className="flex items-center gap-3 text-xs text-txt-tertiary" aria-hidden="true">
              <span className="h-px flex-1 bg-brd" />lub<span className="h-px flex-1 bg-brd" />
            </div>
            <a href="/api/auth/google/start" aria-disabled={busy || undefined}
              className="google-btn flex min-h-[52px] w-full items-center justify-center gap-3 rounded-xl px-4">
              <GoogleG />
              <span>Zaloguj się przez Google</span>
            </a>
          </>
        )}
      </div>
      <p className="mt-6 text-xs text-txt-tertiary">
        Nie masz jeszcze dostępu? Poproś administratora o link zaproszenia.
      </p>
    </AuthShell>
  );
};

export default LoginScreen;
